import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "../../services/api";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./styles/Martyrs.css";

function Martyrs() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 12;

  const [search, setSearch] = useState({
    firstName: "",
    lastName: "",
    fatherName: ""
  });
  const [submittedSearch, setSubmittedSearch] = useState(search);
  const [showFilters, setShowFilters] = useState(false);

  const observer = useRef();
  const lastProfileRef = useCallback((node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    }, { rootMargin: "100px", threshold: 0.1 });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const formatShortDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  }, []);

  const calculateAge = useCallback((birth, death) => {
    const dob = new Date(birth);
    const dod = new Date(death);
    let age = dod.getFullYear() - dob.getFullYear();
    const m = dod.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && dod.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  }, []);

  const capitalizeWords = useCallback((text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/memorialprofile/martyrs", {
        params: { ...submittedSearch, page, pageSize }
      });
      const newProfiles = res.data.data || [];
      setProfiles(prev => page === 1 ? newProfiles : [...prev, ...newProfiles]);
      setHasMore(newProfiles.length === pageSize);
    } catch (err) {
      console.error("Profillər yüklənmədi:", err);
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, page, pageSize]);

  useEffect(() => {
    loadProfiles();
  }, [page, submittedSearch]);

  const handleSearchChange = useCallback((e) => {
    const { name, value } = e.target;
    setSearch(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    setProfiles([]);
    setPage(1);
    setHasMore(true);
    setSubmittedSearch(search);
  }, [search]);

  const hasActiveSearch = search.firstName || search.lastName || search.fatherName;

  const profileCards = useMemo(() => {
    return profiles.map((profile, idx) => {
      const isLast = profiles.length === idx + 1;
      const age = calculateAge(profile.dateOfBirth, profile.dateOfDeath);

      return (
        <Link
          to={`/merhum/${profile.id}`}
          key={profile.id}
          className="mt-card"
          ref={isLast ? lastProfileRef : null}
          style={{ '--delay': `${(idx % 12) * 0.05}s` }}
        >
          <div className="mt-card__img">
            <img
              src={profile.photoUrl ? `https://api.heyatyolu.az${profile.photoUrl}` : "/placeholder.jpg"}
              alt={`${profile.firstName} ${profile.lastName}`}
              loading="lazy"
              onError={(e) => { e.target.src = "/placeholder.jpg"; }}
            />
            <div className="mt-card__flag">🇦🇿</div>
            <div className="mt-card__age-badge">
              <span>{age}</span>
            </div>
          </div>

          <div className="mt-card__body">
            <h3 className="mt-card__name">
              {capitalizeWords(`${profile.firstName} ${profile.fatherName} ${profile.lastName}`)}
            </h3>

            <div className="mt-card__dates">
              <span>{formatShortDate(profile.dateOfBirth)}</span>
              <span className="mt-card__dash">—</span>
              <span>{formatShortDate(profile.dateOfDeath)}</span>
            </div>

            {profile.birthPlace && (
              <p className="mt-card__location">
                <i className="fas fa-map-marker-alt"></i> {profile.birthPlace}
              </p>
            )}

            <div className="mt-card__footer">
              <span className="mt-card__badge">Şəhid</span>
              <span className="mt-card__link">Ətraflı →</span>
            </div>
          </div>
        </Link>
      );
    });
  }, [profiles, lastProfileRef, capitalizeWords, formatShortDate, calculateAge]);

  useEffect(() => {
    return () => { if (observer.current) observer.current.disconnect(); };
  }, []);

  const isFirstLoad = page === 1 && profiles.length === 0 && loading;

  return (
    <div className="mt-page">
      <Helmet>
        <title>Şəhidlər – HeyatYolu</title>
        <meta name="description" content="Vətənin azadlığı uğrunda canını fəda etmiş şəhidlərimizin əziz xatirəsini yaşadan profilləri kəşf edin." />
        <meta name="keywords" content="şəhid, xatirə, heyatyolu, vətən, igid, qəhrəman, memorial, profil, azərbaycan" />
        <meta property="og:title" content="Şəhidlər – HeyatYolu" />
        <meta property="og:description" content="Azərbaycanın igid şəhidlərini yad edin və onların həyatına nəzər salın." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* HEADER */}
      <header className="mt-header">
        <div className="mt-header__flag-bg">
          <div className="mt-header__flag-stripe mt-header__flag-stripe--blue"></div>
          <div className="mt-header__flag-stripe mt-header__flag-stripe--red"></div>
          <div className="mt-header__flag-stripe mt-header__flag-stripe--green"></div>
        </div>
        <div className="mt-header__overlay"></div>
        <div className="mt-header__inner">
          <div className="mt-header__emblem">
            <span className="mt-header__emblem-icon">☪</span>
          </div>
          <h1 className="mt-header__title">Şəhidlərimizə Əbədi Hörmət</h1>
          <p className="mt-header__sub">
            Vətənin azadlığı uğrunda canını fəda etmiş igidlərimizin xatirəsi əbədidir
          </p>
          <div className="mt-header__flag-line">
            <span></span><span></span><span></span>
          </div>
        </div>
      </header>

      {/* SEARCH */}
      <section className="mt-toolbar">
        <form onSubmit={handleSearch} className="mt-search">
          <div className="mt-search__main">
            <i className="fas fa-search mt-search__icon"></i>
            <input
              type="text"
              name="firstName"
              placeholder="Şəhidin adı ilə axtar..."
              value={search.firstName}
              onChange={handleSearchChange}
              className="mt-search__input"
            />
            <button
              type="button"
              className={`mt-search__toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              title="Ətraflı axtarış"
            >
              <i className="fas fa-sliders-h"></i>
            </button>
            <button type="submit" className="mt-search__btn" disabled={loading}>
              {loading ? <i className="fas fa-spinner fa-spin"></i> : "Axtar"}
            </button>
          </div>

          {showFilters && (
            <div className="mt-search__expanded">
              <input
                type="text"
                name="fatherName"
                placeholder="Ata adı..."
                value={search.fatherName}
                onChange={handleSearchChange}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Soyad..."
                value={search.lastName}
                onChange={handleSearchChange}
              />
            </div>
          )}
        </form>

        {hasActiveSearch && (
          <div className="mt-active-filters">
            <span className="mt-chip">
              Axtarış aktiv
              <button onClick={() => {
                setSearch({ firstName: "", lastName: "", fatherName: "" });
                setSubmittedSearch({ firstName: "", lastName: "", fatherName: "" });
                setProfiles([]);
                setPage(1);
                setHasMore(true);
              }}>×</button>
            </span>
          </div>
        )}
      </section>

      {/* RESULTS */}
      <section className="mt-results">
        {isFirstLoad ? (
          <div className="mt-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="mt-skeleton">
                <div className="mt-skeleton__img"></div>
                <div className="mt-skeleton__body">
                  <div className="mt-skeleton__line mt-skeleton__line--long"></div>
                  <div className="mt-skeleton__line mt-skeleton__line--medium"></div>
                  <div className="mt-skeleton__line mt-skeleton__line--short"></div>
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="mt-empty">
            <div className="mt-empty__icon">🇦🇿</div>
            <h3>Şəhid profili tapılmadı</h3>
            <p>Başqa adlarla axtarmağı yoxlayın</p>
            <button
              onClick={() => {
                setSearch({ firstName: "", lastName: "", fatherName: "" });
                setSubmittedSearch({ firstName: "", lastName: "", fatherName: "" });
                setProfiles([]);
                setPage(1);
                setHasMore(true);
              }}
              className="mt-empty__btn"
            >
              Hamısını göstər
            </button>
          </div>
        ) : (
          <div className="mt-grid">
            {profileCards}
          </div>
        )}
      </section>

      {/* LOADING MORE */}
      {loading && !isFirstLoad && (
        <div className="mt-loading-more">
          <div className="mt-loading-more__spinner"></div>
          <span>Daha çox yüklənir...</span>
        </div>
      )}

      {/* END */}
      {profiles.length > 0 && !hasMore && (
        <div className="mt-end">
          <span className="mt-end__flag">🇦🇿</span>
          <p>Ruhları şad olsun</p>
        </div>
      )}
    </div>
  );
}

export default Martyrs;